import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car, AlertCircle, Clock, Plus, TrendingUp, BarChart3, Gauge,
} from "lucide-react";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/Layout";
import { VehicleCard, VehicleCardSkeleton } from "../components/VehicleCard";
import { EmptyState, ErrorState } from "../components/EmptyState";
import { Button } from "../components/UIKit";
import { mapNetworkError } from "../lib/fleet";

/**
 * Dashboard — Analytical view (was the previous Home).
 * Shows counters, aggregated stats, fleet distribution and full vehicle grid.
 */
export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const dashResp = await api.get("/dashboard");
      setData(dashResp.data);
      setErrorMsg(null);
    } catch (e) {
      setErrorMsg(mapNetworkError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const displayName = (user?.name || "").trim();
  const greeting = displayName ? `Analyse de la flotte de ${displayName.split(" ")[0]}` : "Analyse de votre flotte";
  const hasVehicles = (data?.total_vehicles ?? 0) > 0;

  const distribution = useMemo(() => {
    if (!data?.vehicles) return { green: 0, orange: 0, red: 0, unknown: 0 };
    const buckets = { green: 0, orange: 0, red: 0, unknown: 0 };
    data.vehicles.forEach((v) => {
      const lvl = v?.health?.level;
      if (lvl === "green") buckets.green += 1;
      else if (lvl === "orange") buckets.orange += 1;
      else if (lvl === "red") buckets.red += 1;
      else buckets.unknown += 1;
    });
    return buckets;
  }, [data]);

  const avgScore = useMemo(() => {
    if (!data?.vehicles) return null;
    const scored = data.vehicles.map((v) => v?.health?.score).filter((s) => typeof s === "number");
    if (!scored.length) return null;
    return Math.round(scored.reduce((a, b) => a + b, 0) / scored.length);
  }, [data]);

  const totalKm = useMemo(() => {
    if (!data?.vehicles) return 0;
    return data.vehicles.reduce((sum, v) => sum + (v.mileage || 0), 0);
  }, [data]);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }} data-testid="dashboard-page">
      <PageHeader title="Tableau de bord" subtitle={greeting} />

      <div style={{ padding: "0 20px 100px", display: "flex", flexDirection: "column", gap: 20 }}>
        {loading ? (
          <>
            <div style={{ height: 90, background: "var(--mf-bg-muted)", borderRadius: 20 }} />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
              <VehicleCardSkeleton />
              <VehicleCardSkeleton />
            </div>
          </>
        ) : errorMsg && !data ? (
          <ErrorState message={errorMsg} onRetry={() => { setLoading(true); load(); }} testID="dashboard-error" />
        ) : !hasVehicles ? (
          <EmptyState
            testID="dashboard-empty"
            icon={<Car size={64} color="var(--mf-border)" />}
            title="Aucune donnée à analyser"
            subtitle="Ajoutez votre premier véhicule pour commencer à visualiser des statistiques."
            action={
              <Button
                testID="dashboard-empty-add"
                icon={<Plus size={18} />}
                onClick={() => navigate("/vehicles/new")}
                fullWidth={false}
                style={{ paddingLeft: 22, paddingRight: 22 }}
              >
                Ajouter mon premier véhicule
              </Button>
            }
          />
        ) : (
          <>
            <section
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 12,
              }}
              data-testid="dashboard-counters"
            >
              <StatCard label="Véhicules" value={data.total_vehicles} tint="var(--mf-brand)" icon={<Car size={16} />} testID="counter-vehicles" />
              <StatCard label="Score moyen" value={avgScore != null ? `${avgScore}%` : "—"} tint={scoreTint(avgScore)} icon={<TrendingUp size={16} />} testID="counter-score" />
              <StatCard label="Urgent" value={data.urgent_count} tint="var(--mf-urgent-solid)" icon={<AlertCircle size={16} />} testID="counter-urgent" />
              <StatCard label="À prévoir" value={data.upcoming_count} tint="var(--mf-upcoming-solid)" icon={<Clock size={16} />} testID="counter-upcoming" />
              <StatCard label="Kilométrage total" value={`${totalKm.toLocaleString("fr-FR")} km`} tint="var(--mf-text-secondary)" icon={<Gauge size={16} />} testID="counter-km" />
            </section>

            <DistributionCard distribution={distribution} total={data.total_vehicles} />

            <section>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <BarChart3 size={14} color="var(--mf-brand)" />
                <h3 style={{
                  margin: 0, fontSize: 13, fontWeight: 800,
                  color: "var(--mf-text-secondary)", textTransform: "uppercase", letterSpacing: 0.6,
                }}>
                  Tous les véhicules
                </h3>
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 14,
              }} data-testid="dashboard-vehicles-grid">
                {data.vehicles.map((v) => (
                  <VehicleCard key={v.id} vehicle={v} testID={`dashboard-vehicle-${v.id}`} />
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function scoreTint(score) {
  if (score == null) return "var(--mf-text-muted)";
  if (score >= 80) return "var(--mf-ok-solid)";
  if (score >= 60) return "var(--mf-upcoming-solid)";
  return "var(--mf-urgent-solid)";
}

function StatCard({ label, value, tint, icon, testID }) {
  return (
    <div
      data-testid={testID}
      style={{
        background: "var(--mf-bg-card)", borderRadius: 20,
        padding: 16, boxShadow: "var(--mf-shadow)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontSize: 11, color: "var(--mf-text-secondary)", fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {label}
        </span>
        <span style={{
          width: 28, height: 28, borderRadius: 8,
          background: `${tint}22`, color: tint,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>{icon}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: "var(--mf-text)", letterSpacing: -0.6, lineHeight: 1.1 }}>
        {value}
      </div>
    </div>
  );
}

function DistributionCard({ distribution, total }) {
  const items = [
    { key: "green", label: "En bonne santé", value: distribution.green, tint: "var(--mf-ok-solid)" },
    { key: "orange", label: "À surveiller", value: distribution.orange, tint: "var(--mf-upcoming-solid)" },
    { key: "red", label: "Nécessite intervention", value: distribution.red, tint: "var(--mf-urgent-solid)" },
    { key: "unknown", label: "Sans données", value: distribution.unknown, tint: "var(--mf-text-muted)" },
  ].filter((it) => it.value > 0);
  if (!items.length || total === 0) return null;
  return (
    <section
      data-testid="dashboard-distribution"
      style={{
        background: "var(--mf-bg-card)", borderRadius: 20,
        padding: 18, boxShadow: "var(--mf-shadow)",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 800, color: "var(--mf-text-secondary)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>
        Répartition de la flotte
      </div>
      <div style={{ display: "flex", height: 12, borderRadius: 999, overflow: "hidden", background: "var(--mf-bg-muted)" }}>
        {items.map((it) => (
          <div
            key={it.key}
            title={`${it.label}: ${it.value}`}
            style={{ flex: it.value, background: it.tint, transition: "flex 300ms" }}
          />
        ))}
      </div>
      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        {items.map((it) => (
          <div key={it.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 5, background: it.tint }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--mf-text)" }}>{it.value}</span>
            <span style={{ fontSize: 12, color: "var(--mf-text-secondary)" }}>{it.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
